/**
 * @fileoverview React Query client configuration and query key factory.
 *
 * This module provides a centralized QueryClient configuration and type-safe
 * query key factory for consistent cache management across the application.
 *
 * @module lib/queryClient
 */

import { QueryClient } from '@tanstack/react-query';

/**
 * Configured QueryClient instance for the application.
 *
 * Configuration:
 * - staleTime: 30s - Data is considered fresh for 30 seconds
 * - gcTime: 5min - Cache is kept for 5 minutes after last subscriber unmounts
 * - retry: 3 - Failed requests are retried 3 times with exponential backoff
 * - refetchOnWindowFocus: 'always' - Refetch on every window focus (regardless of stale state)
 * - refetchOnReconnect: 'always' - Refetch on every network reconnect (regardless of stale state)
 * - mutations.retry: 0 - Mutations don't retry by default (opt-in for idempotent operations)
 */
export const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      // Data is considered fresh for 30 seconds
      staleTime: 30 * 1000,
      // Cache is kept for 5 minutes after last subscriber unmounts
      gcTime: 5 * 60 * 1000,
      // Retry failed requests 3 times with exponential backoff
      retry: 3,
      retryDelay: (attemptIndex) => Math.min(1000 * 2 ** attemptIndex, 30000),
      // Refetch on every window focus or network reconnect (even if data is fresh)
      refetchOnWindowFocus: 'always',
      refetchOnReconnect: 'always',
      // Don't refetch in background by default
      refetchInterval: false,
    },
    mutations: {
      // Disable mutation retries by default to prevent duplicate side effects.
      // Individual mutations can opt-in to retries where safe (e.g., idempotent operations).
      retry: 0,
    },
  },
});

/**
 * Query key factory for type-safe, consistent cache keys.
 *
 * All query keys follow the pattern: [entity, id?, subEntity?, filters?]
 * This allows for efficient cache invalidation at different granularities:
 * - Invalidate all cases: queryKeys.cases.all
 * - Invalidate specific case: queryKeys.cases.detail(caseId)
 * - Invalidate case messages: queryKeys.cases.messages(caseId)
 *
 * @example
 * ```typescript
 * // Use in useQuery
 * useQuery({
 *   queryKey: queryKeys.cases.messages(caseId),
 *   queryFn: () => fetchMessages(caseId),
 * });
 *
 * // Invalidate all messages for a case
 * queryClient.invalidateQueries({
 *   queryKey: queryKeys.cases.messages(caseId),
 * });
 *
 * // Invalidate everything for a case
 * queryClient.invalidateQueries({
 *   queryKey: queryKeys.cases.detail(caseId),
 * });
 * ```
 */
export const queryKeys = {
  // Case queries
  cases: {
    /** All case-related queries */
    all: ['cases'] as const,

    /**
     * Case list with optional filters
     * @param filters
     * @param filters.status
     * @param filters.page
     * @param filters.pageSize
     */
    list: (filters?: { status?: string; page?: number; pageSize?: number }) =>
      ['cases', 'list', filters] as const,

    /**
     * Single case detail
     * @param caseId
     */
    detail: (caseId: string) => ['cases', caseId] as const,

    /**
     * Messages for a case
     * @param caseId
     */
    messages: (caseId: string) => ['cases', caseId, 'messages'] as const,

    /**
     * Evidence items for a case
     * @param caseId
     */
    evidence: (caseId: string) => ['cases', caseId, 'evidence'] as const,

    /**
     * Evidence requests for a case
     * @param caseId
     */
    evidenceRequests: (caseId: string) =>
      ['cases', caseId, 'evidenceRequests'] as const,

    /**
     * Hypotheses for a case
     * @param caseId
     */
    hypotheses: (caseId: string) => ['cases', caseId, 'hypotheses'] as const,

    /**
     * Timeline events for a case (optionally scoped by limit)
     * @param caseId
     * @param limit
     */
    events: (caseId: string, limit?: number) =>
      limit !== undefined
        ? (['cases', caseId, 'events', { limit }] as const)
        : (['cases', caseId, 'events'] as const),

    /**
     * Report snapshots for a case
     * @param caseId
     */
    reports: (caseId: string) => ['cases', caseId, 'reports'] as const,

    /**
     * Active agent run status for a case
     * @param caseId
     */
    activeAgent: (caseId: string) => ['cases', caseId, 'activeAgent'] as const,
  },

  // Agent run queries
  agentRuns: {
    /**
     * Tool calls for an agent run
     * @param agentRunId
     */
    toolCalls: (agentRunId: string) =>
      ['agentRuns', agentRunId, 'toolCalls'] as const,
  },

  // Admin queries
  admin: {
    /**
     * Admin user list
     * @param filters
     * @param filters.page
     */
    users: (filters?: { page?: number }) => ['admin', 'users', filters] as const,

    /** Tenant settings */
    tenantSettings: () => ['admin', 'tenantSettings'] as const,

    /**
     * Case studies for corpus
     * @param filters
     * @param filters.q
     * @param filters.page
     */
    caseStudies: (filters?: { q?: string; page?: number }) =>
      ['admin', 'caseStudies', filters] as const,

    /**
     * Invites
     * @param filters
     * @param filters.page
     */
    invites: (filters?: { page?: number }) =>
      ['admin', 'invites', filters] as const,
  },
} as const;

/**
 * Type helper to extract query key types.
 */
export type QueryKeys = typeof queryKeys;
